import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { DownloadSimple, DeviceMobile } from "@phosphor-icons/react";

/**
 * Add-to-Home-Screen button for the customer-facing self-order flow.
 *
 * Two paths, one component:
 *  - Android/desktop Chrome/Edge: fires ``beforeinstallprompt``; we
 *    stash the event and trigger it on click.
 *  - iOS Safari: no such event exists. We detect Safari and surface a
 *    tiny hint ("Tap Share → Add to Home Screen") instead.
 *
 * The button hides itself once the app is installed / running in a
 * PWA context so it never nags an already-installed guest.
 *
 * ``variant="floating"`` renders a small pill in the top-right corner
 * (position: fixed) so it's reachable from every view of the ordering
 * flow, not just the landing screen.
 */
const isStandalone = () =>
  window.matchMedia?.("(display-mode: standalone)").matches ||
  window.matchMedia?.("(display-mode: fullscreen)").matches ||
  window.navigator?.standalone === true;

const isIosSafari = () => {
  const ua = window.navigator.userAgent || "";
  const iOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  const webkit = /WebKit/i.test(ua) && !/CriOS|FxiOS|EdgiOS/i.test(ua);
  return iOS && webkit;
};

const InstallAppButton = ({ className = "", variant = "inline" }) => {
  const [prompt, setPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [showIosHint, setShowIosHint] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;

    const onBefore = (e) => {
      e.preventDefault();
      setPrompt(e);
      setVisible(true);
    };
    const onInstalled = () => {
      setVisible(false);
      setPrompt(null);
      toast.success("App installed — launch it from your home screen next time");
    };

    window.addEventListener("beforeinstallprompt", onBefore);
    window.addEventListener("appinstalled", onInstalled);

    // iOS Safari — no beforeinstallprompt exists. Show the hint anyway.
    if (isIosSafari()) setVisible(true);

    return () => {
      window.removeEventListener("beforeinstallprompt", onBefore);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (!visible) return null;

  const handleClick = async () => {
    if (prompt) {
      try {
        prompt.prompt();
        const choice = await prompt.userChoice;
        if (choice.outcome === "accepted") {
          setVisible(false);
        }
        setPrompt(null);
      } catch {
        // User dismissed or browser rejected — leave the button in place
        // so they can try again.
      }
      return;
    }
    // iOS branch — no prompt available; expose a friendly hint.
    setShowIosHint((v) => !v);
  };

  if (variant === "floating") {
    return (
      <>
        <button
          onClick={handleClick}
          className="fixed top-3 right-3 z-50 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary text-primary-foreground shadow-lg hover:shadow-xl active:scale-95 transition-all text-xs font-medium"
          data-testid="install-app-btn-floating"
          aria-label="Install app"
        >
          <DownloadSimple size={14} weight="bold" />
          <span>Install</span>
        </button>
        {showIosHint && (
          <div
            className="fixed top-14 right-3 z-50 max-w-[260px] text-xs bg-white text-foreground shadow-xl border border-border rounded-lg p-3 flex items-start gap-2"
            data-testid="install-app-ios-hint"
          >
            <DeviceMobile size={16} weight="bold" className="text-primary shrink-0 mt-0.5" />
            <div>
              <p className="font-medium mb-0.5">Add to Home Screen</p>
              <p className="text-muted-foreground">
                Tap the Share icon in Safari, then choose <span className="font-medium">Add to Home Screen</span>.
              </p>
              <button className="mt-2 text-[11px] text-primary underline" onClick={() => setShowIosHint(false)}>Got it</button>
            </div>
          </div>
        )}
      </>
    );
  }

  return (
    <div className={`space-y-2 ${className}`} data-testid="install-app-wrapper">
      <Button
        variant="outline"
        className="w-full gap-2 h-11"
        onClick={handleClick}
        data-testid="install-app-btn"
      >
        <DownloadSimple size={18} weight="bold" />
        Install App
      </Button>
      {showIosHint && (
        <div
          className="text-xs bg-secondary/50 border border-border rounded-lg p-3 flex items-start gap-2"
          data-testid="install-app-ios-hint"
        >
          <DeviceMobile size={16} weight="bold" className="text-primary shrink-0 mt-0.5" />
          <div>
            <p className="font-medium mb-0.5">Add to Home Screen</p>
            <p className="text-muted-foreground">
              Tap the Share icon in Safari, then choose <span className="font-medium">Add to Home Screen</span>.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default InstallAppButton;

